import { useState } from "react";
import { CheckCircle2, XCircle, Loader2, ChevronDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../../../foundation/altzero_base/base/components/ui/popover";
import { supabase } from "../../../foundation/altzero_base/base/utils/supabaseClient";
import type { Tender } from "../services/tenderService";

type RetailerThread = {
  retailer_id: string;
  is_replied: boolean;
  retailer: { name: string } | null;
};

// Statuses that imply retailer emails exist even if denorm counts are stale/zero
const RETAILER_ACTIVE_STATUSES = ['waiting_for_retailers_reply', 'retailers_replied'];

export function RetailerResponseCell({ tender }: { tender: Tender }) {
  const [open, setOpen] = useState(false);
  const [threads, setThreads] = useState<RetailerThread[] | null>(null);
  const [loadingThreads, setLoadingThreads] = useState(false);

  const countsSent = tender.retailers_sent_count || 0;
  const countsReplied = tender.retailers_replied_count || 0;
  const hasActivity = countsSent > 0 || RETAILER_ACTIVE_STATUSES.includes(tender.status);

  if (!hasActivity) return <span className="text-muted-foreground text-sm">—</span>;

  const handleOpen = async (isOpen: boolean) => {
    setOpen(isOpen);
    if (isOpen && threads === null && !loadingThreads) {
      setLoadingThreads(true);
      const { data } = await supabase
        .from('tender_email_thread')
        .select('retailer_id, is_replied, retailer:retailer_id(name)')
        .eq('tender_id', tender.id);
      const sorted = (data || []).sort((a: any, b: any) => (b.is_replied ? 1 : 0) - (a.is_replied ? 1 : 0));
      setThreads(sorted as RetailerThread[]);
      setLoadingThreads(false);
    }
  };

  // Once threads loaded, use actual DB counts — denorm fields may be stale
  const actualSent = threads !== null ? threads.length : countsSent;
  const actualReplied = threads !== null ? threads.filter(t => t.is_replied).length : countsReplied;
  const replyPct = actualSent > 0 ? Math.round((actualReplied / actualSent) * 100) : 0;

  return (
    <Popover open={open} onOpenChange={handleOpen}>
      <PopoverTrigger asChild>
        <button className="flex items-center gap-1.5 text-left hover:bg-muted/50 rounded-md px-2 py-1 transition-colors group">
          {actualSent > 0 ? (
            <>
              <div className="flex items-center gap-0.5">
                <span className={`text-sm font-semibold ${actualReplied === actualSent ? 'text-green-600' : actualReplied > 0 ? 'text-amber-600' : 'text-muted-foreground'}`}>
                  {actualReplied}
                </span>
                <span className="text-xs text-muted-foreground">/</span>
                <span className="text-sm font-medium text-foreground">{actualSent}</span>
              </div>
              <span className="text-xs text-muted-foreground">replied</span>
            </>
          ) : (
            <span className="text-xs text-muted-foreground italic">View replies</span>
          )}
          <ChevronDown className="h-3 w-3 text-muted-foreground group-hover:text-foreground transition-colors ml-0.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start" side="bottom">
        <div className="px-3 py-2.5 border-b bg-muted/30">
          <p className="text-xs font-semibold text-foreground">Retailer Response</p>
          {actualSent > 0 && (
            <>
              <div className="flex items-center gap-2 mt-1">
                <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${replyPct === 100 ? 'bg-green-500' : replyPct > 0 ? 'bg-amber-500' : 'bg-gray-300'}`}
                    style={{ width: `${replyPct}%` }}
                  />
                </div>
                <span className="text-xs text-muted-foreground">{replyPct}%</span>
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">{actualReplied} of {actualSent} replied</p>
            </>
          )}
        </div>
        <div className="max-h-56 overflow-y-auto py-1">
          {loadingThreads ? (
            <div className="flex items-center justify-center py-5">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          ) : threads && threads.length > 0 ? (
            threads.map((t) => (
              <div key={t.retailer_id} className="flex items-center gap-2.5 px-3 py-1.5 hover:bg-muted/40">
                {t.is_replied ? (
                  <CheckCircle2 className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
                ) : (
                  <XCircle className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                )}
                <span className="text-sm truncate flex-1">{t.retailer?.name || 'Unknown Retailer'}</span>
                <span className={`text-xs font-medium flex-shrink-0 ${t.is_replied ? 'text-green-600' : 'text-muted-foreground'}`}>
                  {t.is_replied ? 'Replied' : 'Pending'}
                </span>
              </div>
            ))
          ) : (
            <p className="text-xs text-muted-foreground text-center py-4">No email threads found</p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
